import { useContext, useMemo, useEffect, useCallback, useState } from "react"
import PropTypes from "prop-types"
import { isEqual, keys, chain } from "lodash"
import moment from "moment"
import { Box } from "@mui/material"
import Error from "components/error"
import { ERROR, OK } from "helpers/data-statuses"
import { useMetricSelectorMetrics } from "helpers/metrics/hooks"
import { recursiveReduce, getCustomSelectedMetrics } from "helpers/metrics"
import { usePositionSetBetaInstrument, useColumnOrderOptions } from "connected-components/asset-view/helpers"
import { useInstrumentsWithQueryOptions, useAvailableGroupByOptions, getOrderedInstrumentColumns } from "connected-components/dashboard/helpers"
import useAssetViewContext from "connected-components/dashboard/context/asset-view"
import { combineTanStackStatuses, DATA_STATUSES } from "helpers/data-statuses"
import { useHasUnexpandedComposites } from "resources/portfolios"
import { useModelMetadata } from "resources/models"
import { useTemplates } from "resources/templates"
import { useTemplateMetadata } from "resources/templates"
import { CONTENT_SET_METADATA } from "resources/content-sets"
import { LONG_SHORT_GROUPING, OVERWEIGHT_UNDERWEIGHT_GROUPING } from "helpers/rollups"
import { useClassifications } from "resources/classifications"
import { useResourcesList } from "resources/resource"
import { useAnalytics } from "connected-components/analytics"
import { ASSET_VIEW_EVENTS } from "./analytics/events"
import { AssetViewAnalyticsProvider } from "./analytics/provider"
import AssetViewToolbar from "./components/asset-view-toolbar"
import exposureTemplate from "./templates/exposure"
import riskTemplate from "./templates/risk"
import performanceTemplate from "./templates/performance"
import betaTemplate from "./templates/beta"
import customTemplate from "./templates/custom"
import contentSetTemplate from "./templates/content-set"
import marginalContributionTemplate from "./templates/marginal-contribution"
import factorSensitivityTemplate from "./templates/factor-sensitivity"
import { TradeContext } from "connected-components/dashboard/context/trade"
import { ColumnOrderProvider } from "./context/column-order"
import { COLUMN_ORDER_OPTIONS_ID, useHighlightedTradeRowIds } from "./helpers"

import "./style.scss"

function makeViewTemplates(contentSets) {
  const contentSetTemplates = chain(contentSets)
    .keyBy("id")
    .mapValues(() => contentSetTemplate)
    .value()

  return {
    exposure: exposureTemplate,
    performance: performanceTemplate,
    risk: riskTemplate,
    beta: betaTemplate,
    marginalContribution: marginalContributionTemplate,
    predictedFactorSensitivity: factorSensitivityTemplate,
    historicalFactorSensitivity: factorSensitivityTemplate,
    ...contentSetTemplates,
  }
}

export default function AssetViewWrapper(props) {
  const { context: parentContext, contentSets } = props
  const [AssetViewContext, contextValue] = useAssetViewContext(parentContext)
  const { trades, secondInstrument: rebalanced, modelId } = contextValue
  const highlightedRowIds = useHighlightedTradeRowIds(trades, rebalanced)

  const { view, setView } = contextValue
  const [templates, templatesStatus] = useTemplates()
  const viewTemplates = useMemo(() => makeViewTemplates(contentSets), [contentSets])
  const [modelMetadata, modelMetadataStatus] = useModelMetadata(modelId)

  // If the selected view is not a standard template and not an existing custom template, set it to default custom
  // This is to prevent the user from seeing a template that doesn't exist
  // We want to do this in the AssetViewWrapper to avoid multiple renders becasue we change the view
  useEffect(() => {
    const isNotStandardTemplate = !keys(viewTemplates).includes(view)
    const isNotExistingCustomTemplate = !templates?.find(template => template.id === view)
    if (templatesStatus === OK && isNotStandardTemplate && isNotExistingCustomTemplate) {
      setView("custom")
    }
  }, [templates, view, setView, templatesStatus, viewTemplates])

  const status = combineTanStackStatuses([templatesStatus, modelMetadataStatus])

  if (status === ERROR) {
    return <Error />
  }

  return <AssetViewContext.Provider value={contextValue}>
    <AssetViewAnalyticsProvider>
      <AssetView
        {...{
          ...props,
          templates,
          templatesStatus,
          highlightedRowIds,
          context: AssetViewContext,
          viewTemplates,
          modelMetadata,
        }}
      />
    </AssetViewAnalyticsProvider>
  </AssetViewContext.Provider>
}

AssetViewWrapper.propTypes = {
  context: PropTypes.object.isRequired,
  contentSets: PropTypes.arrayOf(CONTENT_SET_METADATA).isRequired,
}

function AssetView(props) {
  const { highlightedRowIds, context, instrumentsWithMetadata, hasMultipleInstruments, templates, templatesStatus, contentSets, viewTemplates, modelMetadata } = props
  const assetViewContext = useContext(context)
  const { setAssetViewGroupBy: setTradePanelAssetViewGroupBy } = useContext(TradeContext)
  const { modelId, endDate, view, groupBy, setGroupBy, showNotHeld, setShowNotHeld, instrument: original, secondInstrument: rebalanced, comparisonInstrument: comparison, base, filter, normalization, selectedMode, isExpanded, setExpanded, actionsBase, columnOrder, setColumnOrder, isEndVsStartDateComparison } = assetViewContext

  const [selectedMetrics, setSelectedMetrics] = useState(null)

  const analytics = useAnalytics()

  // [TODO] [APP-1129] make it work for non-portfolios
  const portfolioId = original?.type === "PORTFOLIO" && original?.id
  const [hasUnexpandedComposites] = useHasUnexpandedComposites(modelId, portfolioId, moment(endDate))
  const hasMultipleUniqueInstruments = hasMultipleInstruments && !isEndVsStartDateComparison
  const [availableGroupByOptions, availableGroupByOptionsStatus] = useAvailableGroupByOptions(original, endDate, { isActive: Boolean(base), hasMultipleUniqueInstruments })
  const templateId = view === "custom" || viewTemplates[view] ? null : view
  const [selectedTemplateMetadata, selectedTemplateMetadataStatus] = useTemplateMetadata(templateId)
  const selectedContentSetMetadata = useMemo(() => contentSets.find(contentSet => contentSet.id === view), [contentSets, view])
  const [classifications, classificationsStatus] = useClassifications()
  const [forecasts, forecastsStatus] = useResourcesList("forecast")

  const instrumentOptions = { isExpanded, normalization, filter }
  const instrumentsWithQueryOptions = useInstrumentsWithQueryOptions({ original, rebalanced, comparison }, selectedMode, instrumentOptions)
  const instrumentColumns = useMemo(() => getOrderedInstrumentColumns(selectedMode), [selectedMode])
  const isActive = Boolean(base)

  const betaInstrument = usePositionSetBetaInstrument(instrumentsWithMetadata.original, modelMetadata)
  const [availableMetrics, availableMetricsStatus] = useMetricSelectorMetrics(modelMetadata.id, betaInstrument, isEndVsStartDateComparison)
  const flattenedMetrics = useMemo(() => recursiveReduce(availableMetrics), [availableMetrics])
  const customSelectedMetrics = useMemo(() => getCustomSelectedMetrics(selectedMetrics, flattenedMetrics, modelMetadata, instrumentsWithMetadata, forecasts, isEndVsStartDateComparison), [selectedMetrics, flattenedMetrics, modelMetadata, instrumentsWithMetadata, forecasts, isEndVsStartDateComparison])

  const isCustomTemplate = view === "custom" || !viewTemplates[view]
  const selectedView = isCustomTemplate ? "custom" : view
  const { options: columnOrderOptions, order } = useColumnOrderOptions(selectedView, selectedMode)

  const hasPerformanceMetrics = useMemo(() => {
    return isCustomTemplate && customSelectedMetrics?.some(metric => metric.prefix === "performance")
  }, [isCustomTemplate, customSelectedMetrics])

  const handleResetLongShortGroupBy = useCallback(() => {
    if (isActive && groupBy.id === "LONG_SHORT") {
      setGroupBy(OVERWEIGHT_UNDERWEIGHT_GROUPING)
    }
    if (!isActive && groupBy.id === "OVERWEIGHT_UNDERWEIGHT") {
      setGroupBy(LONG_SHORT_GROUPING)
    }
  }, [groupBy, setGroupBy, isActive])

  const handleResetAttributeTagGroupBy = useCallback(() => {
    if (!isActive && !hasMultipleUniqueInstruments) {
      return
    }
    const isAttributeOrTag = groupBy.isTag || groupBy.isPositionAttribute
    if (isAttributeOrTag) {
      setGroupBy(null)
    }
  }, [groupBy, setGroupBy, isActive, hasMultipleUniqueInstruments])

  // Analytics-enhanced callbacks
  const handleGroupingChange = useCallback((newGrouping) => {
    if (!newGrouping) {
      analytics.track(ASSET_VIEW_EVENTS.GROUPING_CLEARED, { groupBy: null, groupByType: null })
    } else {
      analytics.track(ASSET_VIEW_EVENTS.GROUPING_CHANGED, {
        groupBy: newGrouping?.id,
        groupByType: newGrouping?.type,
      })
    }
    setGroupBy(newGrouping)
  }, [analytics, setGroupBy])

  const handleExpansionToggle = useCallback((newExpanded) => {
    analytics.track(ASSET_VIEW_EVENTS.EXPANSION_TOGGLED, { isExpanded: newExpanded })
    setExpanded(newExpanded)
  }, [analytics, setExpanded])

  useEffect(() => {
    setTradePanelAssetViewGroupBy(groupBy)
  }, [groupBy, setTradePanelAssetViewGroupBy])

  // Validate that the saved groupBy preference is available in current options
  useEffect(() => {
    if (groupBy && availableGroupByOptions && availableGroupByOptions.length > 0) {
      const isGroupByAvailable = availableGroupByOptions.some(rollup =>
        rollup.options
          ? rollup.options.some(option => isEqual(option, groupBy))
          : isEqual(rollup, groupBy),
      )
      if (!isGroupByAvailable) {
        setGroupBy(null)
      }
    }
  }, [groupBy, availableGroupByOptions, setGroupBy])

  useEffect(() => {
    if (groupBy) {
      handleResetLongShortGroupBy()
      handleResetAttributeTagGroupBy()
    }
  }, [groupBy, handleResetLongShortGroupBy, handleResetAttributeTagGroupBy])

  useEffect(() => {
    if ([COLUMN_ORDER_OPTIONS_ID.DEFAULT, COLUMN_ORDER_OPTIONS_ID.CUSTOM].includes(columnOrder)) {
      return
    }
    if (order) {
      setColumnOrder(order)
    }
  }, [columnOrder, setColumnOrder, order])

  const status = combineTanStackStatuses([selectedTemplateMetadataStatus, availableGroupByOptionsStatus, availableMetricsStatus, classificationsStatus, forecastsStatus])

  if (status === ERROR) {
    return <Error />
  }

  let template = viewTemplates[view]
  if (!template) {
    template = customTemplate
  }

  const headerContent = template.Header && <template.Header {...{ templateMetadata: selectedTemplateMetadata, modelMetadata, selectedContentSetMetadata, context, customSelectedMetrics }} />

  const disableActiveTrading = base && actionsBase && !isEqual(base, actionsBase)

  const isTemplatesLoading = templatesStatus !== OK
  const isGroupingLoading = availableGroupByOptionsStatus !== OK

  const toolbarProps = {
    context,
    templates,
    contentSets,
    isCustomTemplate,
    headerContent,
    columnOrder,
    setColumnOrder,
    columnOrderOptions,
    base,
    showNotHeld,
    setShowNotHeld,
    groupBy,
    handleGroupingChange,
    availableGroupByOptions,
    hasMultipleInstruments,
    isExpanded,
    handleExpansionToggle,
    hasUnexpandedComposites,
    isTemplatesLoading,
    isGroupingLoading,
    hasPerformanceMetrics,
  }

  const content = <Box className="asset-view">
    <AssetViewToolbar {...toolbarProps} />
    <ColumnOrderProvider columnOrder={columnOrder} onColumnOrderChange={setColumnOrder}>
      <template.Component
        modelMetadata={modelMetadata}
        templateMetadata={selectedTemplateMetadata}
        instrumentsWithMetadata={instrumentsWithMetadata}
        instrumentsWithQueryOptions={instrumentsWithQueryOptions}
        highlightedRowIds={highlightedRowIds}
        disableActiveTrading={disableActiveTrading}
        instrumentColumns={instrumentColumns}
        availableMetrics={availableMetrics}
        flattenedMetrics={flattenedMetrics}
        classifications={classifications}
        betaInstrument={betaInstrument}
        forecasts={forecasts}
        selectedMetrics={selectedMetrics}
        setSelectedMetrics={setSelectedMetrics}
        customSelectedMetrics={customSelectedMetrics}
        {...props}
      />
    </ColumnOrderProvider>
  </Box>

  if (template.Context) {
    return <template.Context>
      {content}
    </template.Context>
  }

  return content
}

AssetView.propTypes = {
  context: PropTypes.object.isRequired,
  highlightedRowIds: PropTypes.arrayOf(PropTypes.string),
  instrumentsWithMetadata: PropTypes.shape({
    original: PropTypes.object,
    rebalanced: PropTypes.object,
    comparison: PropTypes.object,
    base: PropTypes.object,
  }).isRequired,
  hasMultipleInstruments: PropTypes.bool.isRequired,
  templates: PropTypes.arrayOf(PropTypes.object),
  templatesStatus: PropTypes.oneOf(DATA_STATUSES),
  contentSets: PropTypes.arrayOf(CONTENT_SET_METADATA).isRequired,
  viewTemplates: PropTypes.object.isRequired,
  modelMetadata: PropTypes.object.isRequired,
}
